import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";

/**
 * usePushActionListener — Écoute les messages postMessage du Service Worker
 * envoyés lorsqu'une action inline ([✓ Envoyer] / [✏️ Modifier]) est tappée
 * dans une notification push.
 *
 * Format attendu :
 *   { type: 'push_action', action: 'send' | 'edit' | string, url: string, data: object }
 *
 * Actions reconnues :
 *  - 'send' → invoke send-pending-email avec data.pending_email_id
 *  - 'edit' → router.push(data.url ou /pending-email/:id)
 *  - 'navigate' (cas notif sans action mais URL différente) → router.push(url)
 *
 * Telemetry : push_action_clicked { action, push_type, has_pending_email }
 *
 * À monter une seule fois dans le layout authentifié.
 */
export function usePushActionListener(): void {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    async function handleMessage(event: MessageEvent): Promise<void> {
      const msg = event.data as
        | {
            type?: string;
            action?: string;
            url?: string;
            data?: Record<string, unknown>;
          }
        | undefined;
      if (!msg) return;

      // Navigation simple (sans action)
      if (msg.type === "navigate" && typeof msg.url === "string") {
        navigate(msg.url);
        return;
      }

      if (msg.type !== "push_action") return;
      const action = typeof msg.action === "string" ? msg.action : "";
      const data = msg.data ?? {};
      const pendingId =
        typeof data.pending_email_id === "string" ? (data.pending_email_id as string) : null;
      const pendingWaId =
        typeof data.pending_whatsapp_id === "string" ? (data.pending_whatsapp_id as string) : null;
      const pushType = typeof data.event_followup_type === "string" ? (data.event_followup_type as string) : "unknown";

      trackEvent("push_action_clicked", {
        action,
        push_type: pushType,
        has_pending_email: Boolean(pendingId),
      });

      // ── WhatsApp préparé par Juliette (Lot 2) ──
      if (action === "send" && pendingWaId) {
        try {
          const { error } = await supabase.functions.invoke("send-pending-whatsapp", {
            body: { pending_whatsapp_id: pendingWaId },
          });
          if (error) throw new Error(error.message);
          toast({ title: "WhatsApp envoyé", description: "Le message a été expédié." });
        } catch (err) {
          toast({
            title: "Envoi échoué",
            description: err instanceof Error ? err.message : "Erreur inconnue",
            variant: "destructive",
          });
          navigate("/");
        }
        return;
      }

      if (action === "edit" && pendingWaId) {
        navigate("/");
        return;
      }

      if (action === "send" && pendingId) {
        try {
          const { error } = await supabase.functions.invoke("send-pending-email", {
            body: { pending_email_id: pendingId },
          });
          if (error) throw new Error(error.message);
          toast({
            title: "Mail envoyé",
            description: "La relance a été expédiée.",
          });
        } catch (err) {
          toast({
            title: "Envoi échoué",
            description: err instanceof Error ? err.message : "Erreur inconnue",
            variant: "destructive",
          });
          if (pendingId) navigate(`/pending-email/${pendingId}`);
        }
        return;
      }

      if (action === "edit" && pendingId) {
        navigate(`/pending-email/${pendingId}`);
        return;
      }

      // Fallback : si une URL est fournie on l'utilise
      if (typeof msg.url === "string" && msg.url.length > 0) {
        navigate(msg.url);
      }
    }

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [navigate, toast]);
}
